// Ask Vera usage stats (VRG-227). API client for the read-only internal aggregate.
//
// Types are declared locally rather than imported from `@repo/types`, matching
// `tourAnalyticsApi.ts` — apps/platform does not depend on that package. The canonical
// definitions live in `apps/api/src/features/ask/ask-stats.types.ts`; keep the two in step.

import { createApiCall } from './index'

export interface AskStatsQuestionCount {
  question: string
  count: number
}

export interface AskStatsMetricCount {
  metric: string
  count: number
}

export interface AskStatsSlugCount {
  slug: string
  count: number
}

export interface AskStatsTermCount {
  termId: string
  count: number
}

/**
 * 1e9. Cost crosses the wire as INTEGER nano-USD and is converted only for display.
 *
 * Not micro-USD, the usual billing unit: at $0.30 per 1M input tokens a single token is
 * 0.3 µUSD, so the very first token is already fractional and float error compounds
 * through a sum over millions of rows. In nano-USD the rates are exactly 300 and 2500 per
 * token, so the whole chain is exact integer arithmetic.
 */
export const NANO_USD_PER_USD = 1_000_000_000

/**
 * What the cost figures are DERIVED FROM, shipped alongside them so the UI can be honest
 * about it rather than presenting a bill.
 *
 * `basis` is the literal `'estimate'` rather than a boolean because a boolean named
 * `isEstimate` reads ambiguously in JSON and inverts badly if a real billing-export
 * source is ever added.
 */
export interface AskCostPricingBasis {
  basis: 'estimate'
  assumedModel: string
  /**
   * FALSE today. `ask_query_log` has no `model` column, so every row is priced as
   * `assumedModel` whether or not it was answered by that model. Read this before
   * presenting the number as authoritative for a mixed-model period.
   */
  modelAttributionAvailable: boolean
  inputUsdPerMillionTokens: number
  outputUsdPerMillionTokens: number
  inputNanoUsdPerToken: number
  outputNanoUsdPerToken: number
  /** Reasoning tokens bill at the OUTPUT rate; there is no separate thinking price. */
  thinkingTokensBilledAtOutputRate: boolean
  sourceUrl: string
  /** `YYYY-MM-DD` the rates were read from `sourceUrl`. */
  ratesRetrievedOn: string
}

export interface AskStatsCompanyRow {
  companyId: string
  companyName: string | null
  questionCount: number
  inputTokens: number
  outputTokens: number
  /** Integer nano-USD. Divide by `NANO_USD_PER_USD` to display. */
  estimatedCostNanoUsd: number
}

export interface AskStats {
  computedAt: string
  totalQuestions: number
  coverageFailures: {
    noDocs: AskStatsQuestionCount[]
    noData: AskStatsQuestionCount[]
  }
  whatPeopleAsk: {
    metrics: AskStatsMetricCount[]
    citedDocs: AskStatsSlugCount[]
    glossaryTerms: AskStatsTermCount[]
  }
  adoption: {
    totalCompanies: number
    totalUsers: number
    perCompany: AskStatsCompanyRow[]
    repeatCompanyRate: number
  }
  cost: {
    totalInputTokens: number
    totalOutputTokens: number
    avgTokensPerQuestion: number
    monthlyPoolCeiling: number
    perCompany: AskStatsCompanyRow[]
    /** Integer nano-USD, SUMMED FROM THE ROWS rather than recomputed from the token
     *  totals — identical while one rate applies, and the only version that stays
     *  correct once rows can be priced per model. */
    totalEstimatedCostNanoUsd: number
    /** A mean, so fractional nano-USD. 0 when no questions have been asked. */
    avgEstimatedCostNanoUsdPerQuestion: number
    pricing: AskCostPricingBasis
  }
}

/**
 * One company's Ask activity (VRG-515/516).
 *
 * NOT a filtered `AskStats`. The cross-company page answers "where is Vera's
 * documentation failing", so it is built out of coverage failures, cited guides and
 * glossary terms. Scoped to one company those sections are mostly empty and mostly
 * not that company's business, so the endpoint returns a different, smaller shape:
 * how much this company asks, who asks, and what it costs.
 */
export interface AskCompanyStats {
  companyId: string
  computedAt: string
  totalQuestions: number
  distinctUsers: number
  /** Keyed by `outcome` (`answered`, `no_docs`, `no_data`, …). Absent key = zero. */
  byOutcome: Record<string, number>
  topQuestions: AskStatsQuestionCount[]
  /**
   * One row per UTC calendar day that had at least one question — days with none
   * are ABSENT, not zero-filled, so a reader charting this must not assume the
   * array is contiguous.
   *
   * `ask_query_log` is a real append-only event log, so a daily series off it is
   * sound. It carries a 90-day retention constant whose purge is not yet wired,
   * so history currently runs further back than 90 days and will silently start
   * truncating the day that job is scheduled.
   */
  dailySeries: Array<{ date: string; count: number }>
  tokens: { input: number; output: number }
}

/** The plan-pool side, from the live limiter. `null` when the plan lookup failed —
 *  the question counts stand on their own and are not withheld for it. */
export interface AskCompanyUsage {
  used: number
  limit: number
  remaining: number
  isUnlimited: boolean
  isOverLimit: boolean
  resetsAt: string
  tier: string
}

export interface AskCompanyStatsResponse {
  stats: AskCompanyStats
  usage: AskCompanyUsage | null
}

export const askStatsApi = {
  /**
   * Cross-company Ask Vera usage stats. Staff-only server-side
   * (`internal_support_manage`) — a caller without it gets a 403, which the hook
   * surfaces as a permission state rather than an empty one, matching
   * `tourAnalyticsApi.analytics`'s contract exactly.
   */
  async stats(): Promise<AskStats> {
    const response = await createApiCall({ method: 'GET', url: '/internal/ask-stats' })
    if (response.status !== 200) {
      const error = new Error(
        response?.data?.message || 'Failed to load Ask stats',
      ) as Error & { status?: number }
      error.status = response?.status ?? 0
      throw error
    }
    return response.data
  },

  /**
   * One company's Ask activity + its plan pool. Gated on `internal_support_manage`,
   * the same permission as the cross-company page and for the same reason: Ask
   * questions are stored VERBATIM and can carry a brand's commercial detail, so
   * reading them needs the bar that already gates staff reading support bodies.
   */
  async companyStats(companyId: string): Promise<AskCompanyStatsResponse> {
    const response = await createApiCall({
      method: 'GET',
      url: `/companies/admin/${companyId}/ask-stats`,
    })
    if (response.status !== 200) {
      const error = new Error(
        response?.data?.message || 'Failed to load Ask stats',
      ) as Error & { status?: number }
      error.status = response?.status ?? 0
      throw error
    }
    return response.data
  },
}
